import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Globe, Sparkles } from 'lucide-react';
import { useLanguage, LANGUAGES, LANGUAGE_ORDER } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import TemplateRequestModal from './TemplateRequestModal';

// Video Asset
const PIRONE_VIDEO = "/assets/pirone-logo-video.webm";

const Header = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const { language, t, toggleLanguage, setGlobalLanguage } = useLanguage();
  const location = useLocation();
  const langDropdownRef = useRef(null);

  // Main navigation
  const mainNav = [
    { label: t('common.home') || 'Home', href: '/' },
    { label: t('common.services'), href: '/services' },
    { label: t('header.webDev') || 'Web Design', href: '/web-design' },
    { label: t('header.localBusinessShort') || 'Local Business', href: '/new-jersey' },
    { label: t('common.about'), href: '/about' },
    { label: t('common.ourWork') || 'Our Work', href: '/our-work' },
    { label: t('common.contact'), href: '/contact' },
  ];

  // Smooth scroll tracking
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLangDropdownOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLanguageSelect = (langCode) => {
    setGlobalLanguage(langCode);
    setIsLangDropdownOpen(false);
  };

  // Dynamic header styles based on scroll
  const scrollProgress = Math.min(scrollY / 100, 1);
  const headerBg = `rgba(10, 10, 10, ${0.4 + scrollProgress * 0.55})`;
  const headerBlur = `${12 + scrollProgress * 12}px`;
  const borderOpacity = scrollProgress * 0.08;

  return (
    <>
      {/* CSS for glow animation */}
      <style>{`
        @keyframes subtleGlow {
          0%, 100% { 
            text-shadow: 0 0 4px rgba(56, 189, 248, 0.3);
            opacity: 0.7;
          }
          50% { 
            text-shadow: 0 0 8px rgba(56, 189, 248, 0.5), 0 0 16px rgba(56, 189, 248, 0.2);
            opacity: 1;
          }
        }
        .tech-glow {
          animation: subtleGlow 3s ease-in-out infinite;
          color: #38BDF8;
        }
        .tech-glow:hover {
          animation: none;
          text-shadow: 0 0 12px rgba(56, 189, 248, 0.6), 0 0 24px rgba(56, 189, 248, 0.3);
          opacity: 1;
        }
      `}</style>

      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out"
        style={{
          background: headerBg,
          backdropFilter: `blur(${headerBlur}) saturate(180%)`,
          WebkitBackdropFilter: `blur(${headerBlur}) saturate(180%)`,
          borderBottom: `1px solid rgba(255, 255, 255, ${borderOpacity})`,
          boxShadow: scrollProgress > 0.5 ? `0 4px 30px rgba(0, 0, 0, ${scrollProgress * 0.3})` : 'none'
        }}
      >
        {/* Gradient overlay for depth */}
        <div 
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            background: `linear-gradient(180deg, rgba(56, 189, 248, ${scrollProgress * 0.02}) 0%, transparent 100%)`,
            opacity: scrollProgress
          }}
        />

        {/* Main Header */}
        <div className="relative max-w-[1600px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[70px]">
            
            {/* Logo */}
            <Link 
              to="/"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center gap-3 group relative z-10"
              data-testid="header-logo"
            >
              <div className="relative">
                <video 
                  src={PIRONE_VIDEO}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="h-6 sm:h-7 lg:h-8 w-auto transition-all duration-300 group-hover:scale-105"
                  style={{ 
                    filter: `drop-shadow(0 0 ${8 + scrollProgress * 4}px rgba(56, 189, 248, ${0.2 + scrollProgress * 0.1}))`,
                    background: 'transparent',
                    mixBlendMode: 'lighten',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                {/* Fallback logo when video fails */}
                <span 
                  className="hidden text-xl sm:text-2xl font-bold tracking-tight"
                  style={{ 
                    background: 'linear-gradient(135deg, #38BDF8 0%, #A78BFA 50%, #38BDF8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  PIRONE
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center">
              {/* Main Links */}
              <div className="flex items-center">
                {mainNav.map((item, index) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`relative px-4 py-2 text-[13px] font-medium transition-all duration-300 rounded-lg ${
                      location.pathname === item.href 
                        ? 'text-white bg-white/[0.08]' 
                        : item.highlight
                          ? 'tech-glow'
                          : 'text-white/50 hover:text-white hover:bg-white/[0.04]'
                    }`}
                  >
                    {item.label}
                    {/* Active indicator */}
                    {location.pathname === item.href && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] bg-[#38BDF8] rounded-full" />
                    )}
                  </Link>
                ))}
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-white/[0.1] mx-4" />

              {/* Language Selector */}
              <div className="relative" ref={langDropdownRef}>
                <button
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className={`flex items-center gap-2 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-300 ${
                    isLangDropdownOpen 
                      ? 'bg-white/[0.08] text-white' 
                      : 'text-white/50 hover:text-white hover:bg-white/[0.04]'
                  }`}
                  data-testid="global-lang-dropdown"
                >
                  <Globe size={15} className="opacity-70" />
                  <span className="hidden xl:inline">{LANGUAGES[language]?.native || 'EN'}</span>
                  <ChevronDown size={13} className={`opacity-50 transition-transform duration-300 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Language Dropdown */}
                <div 
                  className={`absolute top-full right-0 mt-2 min-w-[200px] max-h-[400px] overflow-y-auto rounded-2xl py-2 transition-all duration-300 ${
                    isLangDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-3 pointer-events-none'
                  }`}
                  style={{
                    background: 'rgba(10, 10, 10, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <div className="px-4 py-2 border-b border-white/[0.06] mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white/30">
                      {t('common.selectLang')}
                    </span>
                  </div>
                  {LANGUAGE_ORDER.map((langCode) => {
                    const lang = LANGUAGES[langCode];
                    if (!lang) return null;
                    return (
                      <button
                        key={langCode}
                        onClick={() => handleLanguageSelect(langCode)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-all duration-200 ${
                          language === langCode 
                            ? 'text-[#38BDF8] bg-[#38BDF8]/[0.08]' 
                            : 'text-white/50 hover:text-white hover:bg-white/[0.04]'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.native}</span>
                        {language === langCode && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#38BDF8]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-white/[0.1] mx-4" />

              {/* CTA Button */}
              <button
                onClick={() => setIsTemplateModalOpen(true)}
                className="group relative flex items-center gap-2 h-10 px-5 text-[13px] font-semibold text-white rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                data-testid="header-template-request"
                style={{
                  background: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 50%, #0284C7 100%)',
                  boxShadow: `0 4px 20px rgba(56, 189, 248, ${0.2 + scrollProgress * 0.15})`
                }}
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Sparkles size={14} className="relative z-10" />
                <span className="relative z-10">{t('header.freeTemplate') || 'Free Template'}</span>
              </button>
            </nav>

            {/* Mobile Controls */}
            <div className="lg:hidden flex items-center gap-2">
              <button 
                onClick={toggleLanguage} 
                className="flex items-center justify-center w-9 h-9 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.05] transition-all"
              >
                <Globe size={16} />
              </button>
              
              <button 
                className="flex items-center justify-center w-9 h-9 rounded-lg text-white hover:bg-white/[0.05] transition-all" 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden absolute top-full left-0 right-0 transition-all duration-500 ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            background: 'rgba(10, 10, 10, 0.98)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <div className="max-w-[1600px] mx-auto px-4 py-6">
            {/* Mobile Nav Links */}
            <nav className="space-y-1 mb-6">
              {mainNav.map((item) => (
                <Link 
                  key={item.label} 
                  to={item.href} 
                  className={`block px-4 py-3 text-base font-medium rounded-xl transition-all ${
                    location.pathname === item.href 
                      ? 'text-white bg-white/[0.08]' 
                      : item.highlight
                        ? 'text-[#38BDF8]/80'
                        : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Language Selector - All 11 Languages */}
            <div className="mb-6 pb-6 border-b border-white/[0.06]">
              <span className="block px-4 text-[11px] font-semibold uppercase tracking-[0.15em] text-white/30 mb-3">
                {t('common.selectLang')}
              </span>
              <div className="grid grid-cols-2 gap-2 px-4">
                {LANGUAGE_ORDER.map((code) => {
                  const lang = LANGUAGES[code];
                  if (!lang) return null;
                  return (
                    <button
                      key={code}
                      onClick={() => handleLanguageSelect(code)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        language === code 
                          ? 'bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/30' 
                          : 'bg-white/[0.03] text-white/50 border border-white/[0.06] hover:bg-white/[0.05]'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="truncate">{lang.native}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Mobile CTA */}
            <div className="px-4">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsTemplateModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 w-full py-3.5 text-base font-semibold text-white rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 50%, #0284C7 100%)'
                }}
              >
                <Sparkles size={16} />
                <span>{t('header.freeTemplate') || 'Free Template Request'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-[70px]" />

      {/* Template Request Modal */}
      <TemplateRequestModal 
        isOpen={isTemplateModalOpen} 
        onClose={() => setIsTemplateModalOpen(false)} 
      />
    </>
  );
};

export default Header;
